/**
 * THE MODULE CATALOGUE — the technical `module_code` the gate checks.
 *
 * 01 §A6 / D12: the gate checks exactly one thing, a technical `module_code`. Resolution is
 * `Subscription → SKU → sku_module → module_code`. **Workstream B never sees a SKU.**
 *
 * The codes below are taken verbatim from the accepted SKU catalogue (01 §4.2), which is the only
 * place in the baseline that maps SKUs to module codes. `02 §5.3` records the legacy source as
 * `glpoint_control_plane.module_catalog`, 15 rows, disposition REUSE + MIGRATE, "extend to all 24
 * enforced modules".
 *
 * `status` matters and is not decoration:
 *   `active`  — the module exists in SaaS Next and may back a route declaration today.
 *   `planned` — the code is registered so a SKU can map to it and so no second namespace is ever
 *               invented (G-06), but **no module code exists**. A `planned` module has no routes.
 *   `retired` — readable forever, never mintable again.
 *
 * Only `company_management` and `billing` are `active`. Declaring a business module active before it
 * has migrated would be the "written, correct, documented as working, and reaching nothing" pattern
 * that G-03 exists to catch — found ten times in Legacy.
 */
function mod(code, displayNameHe, displayNameEn, status, isCompanyScoped, sku, requiresSourceSystem = null) {
    return { code, displayNameHe, displayNameEn, status, isCompanyScoped, requiresSourceSystem, sku };
}
export const MODULE_CATALOGUE = [
    // ── K0 Core / פלטפורמה — always included, never sold alone ────────────────────────────────────
    mod('company_management', 'ניהול חברות', 'Company management', 'active', false, 'K0'),
    // The meter itself. 01 §4.2: "NEVER sellable — it is the meter."
    mod('billing', 'חיוב ומנויים', 'Billing', 'active', false, null),
    // 01 §4.2: "NEVER — platform tool."
    mod('db_browser', 'דפדפן נתונים', 'DB browser', 'planned', false, null),
    // ── K1 דוחות כספיים ───────────────────────────────────────────────────────────────────────────
    mod('financial_reports', 'דוחות כספיים', 'Financial statements', 'planned', true, 'K1'),
    mod('fs', 'דוחות כספיים (SQL)', 'Financial statements (SQL twin)', 'planned', true, 'K1'),
    // ── K2 CFO / תזרים ────────────────────────────────────────────────────────────────────────────
    mod('cfo2', 'CFO ותזרים', 'CFO / cash flow', 'planned', true, 'K2'),
    mod('finsite', 'FINSITE', 'FINSITE', 'planned', true, 'K2', 'finsite'),
    // ── K3 מע"מ ורשויות ───────────────────────────────────────────────────────────────────────────
    mod('vat_control', 'בקרת מע"מ', 'VAT control', 'planned', true, 'K3'),
    mod('authorities_report', 'דיווח לרשויות', 'Authorities reporting', 'planned', true, 'K3'),
    // ── K4 בנק ────────────────────────────────────────────────────────────────────────────────────
    mod('bank_statements_admin', 'ניהול דפי בנק', 'Bank statements administration', 'planned', true, 'K4'),
    // ── K5 כרטיסי אשראי ───────────────────────────────────────────────────────────────────────────
    mod('credit_cards', 'כרטיסי אשראי', 'Credit cards', 'planned', true, 'K5'),
    mod('credit_cards_v2', 'כרטיסי אשראי v2', 'Credit cards v2', 'planned', true, 'K5'),
    mod('credit_cards_v2_admin', 'ניהול כרטיסי אשראי v2', 'Credit cards v2 administration', 'planned', true, 'K5'),
    // ── K6 פקודות יומן / MOVEIN ───────────────────────────────────────────────────────────────────
    mod('je', 'פקודות יומן', 'Journal entries / MOVEIN', 'planned', true, 'K6', 'priority'),
    // ── K7 גבייה ──────────────────────────────────────────────────────────────────────────────────
    mod('collection', 'גבייה', 'Collection', 'planned', true, 'K7'),
    // ── K8 טפסים 126/856 ──────────────────────────────────────────────────────────────────────────
    mod('form_126_856', 'טפסים 126/856', 'Forms 126 / 856', 'planned', true, 'K8'),
    // ── K9 מענקים ─────────────────────────────────────────────────────────────────────────────────
    mod('grants_shaagat_ari', 'מענקים (שאגת הארי)', 'Grants', 'planned', true, 'K9'),
    // ── K10 מכתבי רו"ח ────────────────────────────────────────────────────────────────────────────
    mod('accountant_letters', 'מכתבי רו"ח', 'Accountant letters', 'planned', false, 'K10'),
    // ── K11 לידים · K12 הצעת מחיר — real SKUs, not offered externally (01 §4.2) ────────────────────
    mod('leads', 'לידים ומכירות', 'Leads & sales CRM', 'planned', false, 'K11'),
    mod('pricequote', 'הצעת מחיר', 'Price quote', 'planned', false, 'K12'),
    // ── K13 נוכחות ושכר — dormant/down in Legacy, not priced ───────────────────────────────────────
    mod('attendance', 'נוכחות', 'Attendance', 'planned', false, 'K13'),
    mod('payroll', 'שכר', 'Payroll', 'planned', false, 'K13'),
    // ── Connector — infrastructure. D-24: 24,079 queued, nothing ever ingested. ────────────────────
    mod('connector', 'מחבר מקורות', 'Connector agent', 'planned', false, null),
];
const MODULE_INDEX = new Map(MODULE_CATALOGUE.map((m) => [m.code, m]));
export function findModule(code) {
    return MODULE_INDEX.get(code);
}
/** 04 §3.2 boot rule: "scope in (COMPANY, MODULE) ⇒ module must exist in the module catalogue". */
export function moduleExists(code) {
    return MODULE_INDEX.has(code);
}
//# sourceMappingURL=modules.js.map